/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import { useCallback, useRef } from 'react';
import { useMachine } from '@xstate/react';
import { useToast } from '../../../../shared/Toast';
import { trackAnalyticsEvent } from '../../../../shared/trackAnalyticsEvent';
import { EVALUATIONS_STRINGS as STRINGS } from './evaluations.strings';
import { repositoryReevaluationMachine } from './machines/repositoryReevaluationMachine';
import { RE_EVALUATE_CLICKED_EVENT } from './repositoryReevaluation.constants';
import type { ClickResult } from './repositoryReevaluation.types';

/**
 * The re-evaluation of one repository for the view: whether the action is unavailable, the confirmation dialog, and the
 * commands. `reportedRunning` is what IQ says on the list (a run started elsewhere or before this page was opened);
 * `onSettled` refreshes the list when a run ends.
 */
export function useRepositoryReevaluation(repositoryId: string, reportedRunning: boolean, onSettled: () => void) {
  const toast = useToast();

  // The machine calls the latest callback, so a new function on each render never reaches it stale.
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const track = (result: ClickResult) => trackAnalyticsEvent(RE_EVALUATE_CLICKED_EVENT, { result });

  const [state, send] = useMachine(repositoryReevaluationMachine, {
    context: { ...repositoryReevaluationMachine.context, repositoryId },
    actions: {
      notifyStarted: () => toast.success(STRINGS.reevaluate.started),
      notifyAlreadyRunning: () => toast.info(STRINGS.reevaluate.alreadyRunning),
      notifyStartFailed: (context) => toast.error(STRINGS.reevaluate.errors.start, context.error ?? undefined),
      notifySettled: (context) => {
        switch (context.outcome) {
          case 'completed':
            return toast.success(STRINGS.reevaluate.completed);
          case 'finished':
            return toast.info(STRINGS.reevaluate.finished);
          case 'skipped':
            return toast.warning(STRINGS.reevaluate.skipped);
          case 'failed':
            return toast.error(STRINGS.reevaluate.failed);
          case 'error':
            return toast.error(STRINGS.reevaluate.errors.poll, context.error ?? undefined);
          default:
            // alreadyRunning was announced when the start answered
            return undefined;
        }
      },
      refreshList: () => onSettledRef.current(),
      trackStarted: () => track('started'),
      trackAlreadyRunning: () => track('already-running'),
      trackFailed: () => track('failed'),
    },
  });

  const busy = state.matches('starting') || state.matches('running');

  return {
    /** No new run can start: one is being started or followed here, or IQ reports one on the list. */
    running: busy || reportedRunning,
    confirmOpen: state.matches('confirming'),
    openConfirm: useCallback(() => send({ type: 'OPEN_CONFIRM' }), [send]),
    closeConfirm: useCallback(() => send({ type: 'CLOSE_CONFIRM' }), [send]),
    confirm: useCallback(() => send({ type: 'CONFIRM' }), [send]),
  };
}
